/**
 * Referral program configuration.
 *
 * The loop is double-sided + tiered:
 *   - The invited FRIEND gets a welcome grant when they sign up with a code.
 *   - The REFERRER gets a small Tier-1 grant on the friend's signup (capped — the
 *     anti-abuse lever) and the headline Tier-2 grant when the friend actually
 *     subscribes (paid). The valuable reward only vests on a real paid conversion,
 *     so throwaway accounts can't farm free months.
 *
 * Every value below is DAYS of free Premium granted server-side (written to
 * user_profiles.promotional_premium_until). Tune freely — all referral logic reads
 * these constants, so there are no magic numbers scattered through the code.
 */
export const REFERRAL_REWARDS = {
  /** Free-Premium days the invited friend receives on a qualifying signup. */
  REFERRED_WELCOME_DAYS: 14,
  /** Free-Premium days the referrer receives when a friend signs up (small, capped). */
  REFERRER_SIGNUP_DAYS: 7,
  /** Free-Premium days the referrer receives when a friend's first paid sub lands. */
  REFERRER_SUBSCRIPTION_DAYS: 30,
} as const

/**
 * Anti-abuse cap on the cheap Tier-1 (signup) referrer reward. After this many
 * signup-tier grants, further friend signups still count toward the referrer's
 * stats and still pay the valuable Tier-2 reward on a real conversion, but stop
 * minting free days from signups alone.
 */
export const REFERRER_SIGNUP_REWARD_CAP = 10

/**
 * A referral can only attach to a genuinely NEW account. Accounts older than this
 * (at redeem time) are rejected so existing users can't retroactively claim a code.
 */
export const REFERRED_MAX_ACCOUNT_AGE_DAYS = 14

/** Length of generated referral codes (alphabet below excludes ambiguous chars). */
export const REFERRAL_CODE_LENGTH = 7

/** The reward-ledger / referral-tier discriminator. Mirrors the DB CHECK constraint. */
export type ReferralRewardType =
  | 'referred_welcome'
  | 'referrer_signup'
  | 'referrer_subscription'

/** Reasons redeemReferral can decline, surfaced to the client for messaging. */
export type ReferralRedeemReason =
  | 'invalid_code'
  | 'self_referral'
  | 'already_referred'
  | 'account_too_old'
  | 'error'

/**
 * Canonicalize a (possibly messy) referral code to the stored form: uppercase,
 * alphanumerics only. Returns null for anything that can't be a code. Lives here
 * (zero imports) so it's unit-testable without dragging in the Supabase client.
 */
export function normalizeReferralCode(input: string | null | undefined): string | null {
  if (!input) return null
  const cleaned = input.trim().toUpperCase().replace(/[^0-9A-Z]/g, '')
  if (cleaned.length < 4 || cleaned.length > 16) return null
  return cleaned
}
